<script setup>
import { computed, ref, watch } from 'vue';
import Button from './button/Button.vue';
import ConversationAPI from 'dashboard/api/inbox/conversation';
import { useMapGetter } from 'dashboard/composables/store';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  buttons: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['click', 'close']);

const currentChat = useMapGetter('getSelectedChat');
const pendingScheduledCount = ref(0);

const refreshPendingScheduledCount = async conversationId => {
  if (!conversationId) {
    pendingScheduledCount.value = 0;
    return;
  }
  try {
    const { data } =
      await ConversationAPI.getScheduledMessagesCount(conversationId);
    pendingScheduledCount.value = Number(data?.count) || 0;
  } catch {
    pendingScheduledCount.value = 0;
  }
};

watch(
  () => currentChat.value?.id,
  id => {
    const needsBadge = (props.buttons || []).some(
      button => button.key === 'agendar'
    );
    if (!needsBadge) {
      pendingScheduledCount.value = 0;
      return;
    }
    refreshPendingScheduledCount(id);
  },
  { immediate: true }
);

const handleButtonClick = button => {
  emit('click', button.key);
};

const badgeLabel = count => {
  const n = Number(count) || 0;
  if (n <= 0) return '';
  return n > 99 ? '99+' : String(n);
};

const resolvedButtons = computed(() =>
  (props.buttons || []).map(button => {
    if (button.key !== 'agendar') return button;
    const explicit = Number(button.badge);
    return {
      ...button,
      badge:
        Number.isFinite(explicit) && explicit > 0
          ? explicit
          : pendingScheduledCount.value,
    };
  })
);
</script>

<template>
  <div
    class="flex items-center justify-between px-4 py-2 border-b border-n-weak h-12"
  >
    <div class="flex items-center justify-between gap-2 flex-1">
      <span class="font-medium text-sm text-n-slate-12">{{ title }}</span>
      <div class="flex items-center">
        <div
          v-for="button in resolvedButtons"
          :key="button.key"
          class="relative inline-flex"
        >
          <!-- SiteUp V2: cinza e maior. `ghost` sem cor cai no azul (blue é o
               default do Button), e ícone de cromo em azul disputa atenção com
               ação de verdade. Cor semântica só onde significa algo. -->
          <Button
            v-tooltip="button.tooltip"
            :icon="button.icon"
            ghost
            slate
            md
            @click="handleButtonClick(button)"
          />
          <span
            v-if="badgeLabel(button.badge)"
            data-testid="sidebar-action-badge"
            class="pointer-events-none absolute -right-0.5 -top-0.5 inline-flex min-w-[1rem] items-center justify-center rounded-full bg-n-brand px-1 text-[10px] font-semibold leading-4 text-white"
          >
            {{ badgeLabel(button.badge) }}
          </span>
        </div>
        <Button
          v-tooltip="$t('GENERAL.CLOSE')"
          icon="i-ri-close-line"
          ghost
          slate
          md
          @click="$emit('close')"
        />
      </div>
    </div>
  </div>
</template>
